import { useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';

/**
 * A new build must never reload the app under a supervisor mid-capture.
 * The service worker registers with `prompt`, and this offers the reload.
 *
 * The button must always do something. `updateServiceWorker()` works by
 * messaging the *waiting* worker and reloading when it takes control — so
 * if that worker is already gone, it does nothing at all and the button
 * looks dead. That happens easily: this prompt stays on screen until it is
 * answered, and a tablet left open overnight can have had its waiting
 * worker activated by another tab in the meantime.
 *
 * So: ask for an update check first (in case the browser has not run one),
 * then hand over to the worker, and reload on our own account if control
 * has not changed shortly after. A reload that turns out to be unnecessary
 * costs a second; a button that does nothing costs the user's trust in
 * every other button on the screen.
 */
const HANDOVER_GRACE_MS = 2500;

export function UpdatePrompt() {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({ immediate: true });
  const [busy, setBusy] = useState(false);

  async function reload() {
    setBusy(true);
    try {
      const registration = await navigator.serviceWorker?.getRegistration();
      // No waiting worker yet? The prompt may have outlived one, or the
      // browser may not have checked. Either way, look before reloading.
      if (registration && !registration.waiting) {
        await registration.update().catch(() => undefined);
      }
      await updateServiceWorker(true);
    } catch {
      /* fall through to the reload below */
    }
    // If the new worker took control, the page is already going. If it did
    // not, this is what makes the button honest.
    window.setTimeout(() => window.location.reload(), HANDOVER_GRACE_MS);
  }

  if (!needRefresh) return null;

  return (
    <div
      style={{
        position: 'fixed',
        insetInline: 0,
        bottom: 'var(--sp-4)',
        margin: '0 auto',
        width: 'max-content',
        maxWidth: '90vw',
        zIndex: 300,
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--sp-3)',
        padding: 'var(--sp-2) var(--sp-3)',
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border-medium)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-raised)',
      }}
    >
      <span className="small">A new version is available.</span>
      <button className="btn" style={{ minHeight: 32 }} disabled={busy} onClick={() => void reload()}>
        {busy ? 'Updating…' : 'Reload'}
      </button>
      <button
        className="btn btn--ghost"
        style={{ minHeight: 32 }}
        disabled={busy}
        onClick={() => setNeedRefresh(false)}
      >
        Later
      </button>
    </div>
  );
}
